import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useDebouncedCallback } from 'use-debounce';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  employeeAPI,
  territoriesAPI,
  warehousesAPI,
  vehicleAPI,
  regionAPI,
} from '../services/api.ts';
import toast from 'react-hot-toast';
import { Switch } from '@headlessui/react';
interface SearchableOption {
  work_email?: any;
  vehicle_id?: number;
  id: number;
  name: string;
  display_name?: string;
}
// Define validation schema
const EmployeeSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  work_email: Yup.string().email('Invalid email').required('Email is required'),
  mobile_phone: Yup.string().required('Mobile phone is required'),
  territory_ids: Yup.array()
    .of(Yup.number())
    .min(1, 'At least one territory is required')
    .required('Territory is required'),
  warehouse_ids: Yup.array()
    .of(Yup.number())
    .min(1, 'At least one warehouse is required')
    .required('Warehouse is required'),
  is_territory_manager: Yup.boolean(),
  is_region_manager: Yup.boolean(),
  report_to: Yup.number().when(['is_region_manager'], {
    is: (isRegionManager: boolean) => {
      return isRegionManager; // Only hide manager field for Region Managers
    },
    then: () => Yup.number().notRequired(),
    otherwise: () => Yup.number().required('Manager is required'),
  }),
  vehicle_id: Yup.number().required('Vehicle is required'),
  region_id: Yup.number().required('Region is required'),
  meter_reading: Yup.number().nullable(),
  password: Yup.string().when('id', {
    is: (val: number) => !val,
    then: () =>
      Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Password is required for new employees'),
    otherwise: () => Yup.string().notRequired(),
  }),
});
export const UserForm = ({ initialValues, onSubmit, onCancel }: any) => {
  const { project } = useAuth();
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [territories, setTerritories] = useState<SearchableOption[]>([]);
  const [warehouses, setWarehouses] = useState<SearchableOption[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [managers, setManagers] = useState<SearchableOption[]>([]);
  const [regions, setRegions] = useState<SearchableOption[]>([]);
  const [selectedManagerType, setSelectedManagerType] = useState({
    territoryManager: false,
    regionManager: false,
  });
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState({
    employee: false,
    territory: false,
    warehouse: false,
    vehicle: false,
    manager: false,
    region: false,
  });
  const [selectedTerritories, setSelectedTerritories] = useState<
    SearchableOption[]
  >([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedRegionId, setSelectedRegionId] = useState('');
  const [territorySearchTerm, setTerritorySearchTerm] = useState('');
  const [selectedWarehouses, setSelectedWarehouses] = useState<
    SearchableOption[]
  >([]);
  const [warehouseSearchTerm, setWarehouseSearchTerm] = useState('');
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState<any>('');
  const [showPassword, setShowPassword] = useState(false);
  const formikRef = useRef<any>(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (initialValues.territory_ids?.length) {
          const territoryPromises = initialValues.territory_ids.map(
            (id: number) => territoriesAPI.getById(id)
          );
          const territoryResponses = await Promise.all(territoryPromises);
          const validTerritories = territoryResponses
            .filter((res) => res.success)
            .map((res) => res.data);
          setSelectedTerritories(validTerritories);
        }
        if (initialValues.warehouse_ids?.length) {
          const warehousePromises = initialValues.warehouse_ids.map(
            (id: number) => warehousesAPI.getById(id)
          );
          const warehouseResponses = await Promise.all(warehousePromises);
          const validWarehouses = warehouseResponses
            .filter((res) => res.success)
            .map((res) => res.data);
          setSelectedWarehouses(validWarehouses);
        }
        if (initialValues.vehicle_id) {
          const vehicleRes = await vehicleAPI.getById(initialValues.vehicle_id);
          if (vehicleRes.success && vehicleRes.data) {
            setVehicles([vehicleRes.data]);
            setSelectedVehicle(vehicleRes.data.name);
          }
        }
        if (initialValues.report_to) {
          const managerRes = await employeeAPI.getById(initialValues.report_to);
          if (managerRes.success) {
            setManagers([managerRes.data]);
            setSelectedManager(managerRes.data.name);
          }
        }

        if (initialValues.region_id) {
          const regionRes = await regionAPI.getById(initialValues.region_id);
          if (regionRes.success) {
            setRegions([regionRes.data.region]);
            setSelectedRegion(regionRes.data.region.name);
            setSelectedRegionId(regionRes.data.region.id.toString());
          }
        }

        if (initialValues?.is_territory_manager) {
          setSelectedManagerType((prev) => ({
            ...prev,
            territoryManager: true,
          }));
        }
        if (initialValues?.is_region_manager) {
          setSelectedManagerType((prev) => ({
            ...prev,
            regionManager: true,
          }));
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [initialValues]);

  const handleSearch = useDebouncedCallback(
    async (type: string, searchTerm: string) => {
      if (!searchTerm || searchTerm.length < 2) {
        // Clear results when search term is empty or too short
        switch (type) {
          case 'territory':
            setTerritories([]);
            break;
          case 'warehouse':
            setWarehouses([]);
            break;
          case 'region':
            setRegions([]);
            break;
          case 'vehicle':
            setVehicles([]);
            break;
          case 'manager':
            setManagers([]);
            break;
        }
        return;
      }

      // Get company_id from project
      const company_id = project?.company_id?.id || project?.company_id;

      if (!company_id) {
        console.error('Company ID not found');
        return;
      }

      setSearchLoading((prev) => ({ ...prev, [type]: true }));
      try {
        let response;
        switch (type) {
          case 'territory':
            response = await territoriesAPI.search(searchTerm, company_id);
            if (response.data.success) {
              // filter territorues with selected region
              const filteredTerritories = response.data.data.filter(
                (territory: any) =>
                  territory.region_id === Number(selectedRegionId)
              );

              setTerritories(filteredTerritories);
            }
            break;
          case 'warehouse':
            response = await warehousesAPI.search(searchTerm, company_id);
            if (response.data.success) {
              setWarehouses(response.data.data);
            }
            break;
          case 'region':
            response = await regionAPI.search(searchTerm);
            if (response.success) {
              setRegions(response.data.regions);
            }
            break;
          case 'vehicle':
            response = await vehicleAPI.search(searchTerm, company_id);
            if (response.data.success) {
              setVehicles(response.data.data);
            }
            break;
          case 'manager':
            response = await employeeAPI.search(searchTerm, company_id);
            if (response.success) {
              // Filter out the current employee from manager search results if editing
              const filteredManagers = response.data.filter(
                (emp: any) => !initialValues.id || emp.id !== initialValues.id
              );
              setManagers(filteredManagers);
            }
            break;
        }
      } catch (error) {
        console.error(`Error searching ${type}:`, error);
      } finally {
        setSearchLoading((prev) => ({ ...prev, [type]: false }));
      }
    },
    800
  );

  // Debounced search handlers for individual fields - only handle search, not state updates
  const debouncedRegionSearch = useDebouncedCallback((value: string) => {
    handleSearch('region', value);
  }, 300);

  const debouncedTerritorySearch = useDebouncedCallback((value: string) => {
    handleSearch('territory', value);
  }, 300);

  const debouncedWarehouseSearch = useDebouncedCallback((value: string) => {
    handleSearch('warehouse', value);
  }, 300);

  const debouncedVehicleSearch = useDebouncedCallback((value: string) => {
    handleSearch('vehicle', value);
  }, 300);
  // Update employee search handler
  const handleEmployeeSearch = useDebouncedCallback(
    async (searchTerm: string) => {
      if (!searchTerm || searchTerm.length < 2) {
        setSearchResults([]);
        return;
      }
      setSearchLoading((prev) => ({ ...prev, employee: true }));
      try {
        const company_id = project?.company_id;
        const response = await employeeAPI.search(searchTerm, company_id);
        if (response.success) {
          setSearchResults(response.data);
        }
      } catch (error) {
        console.error('Error searching employees:', error);
      } finally {
        setSearchLoading((prev) => ({ ...prev, employee: false }));
      }
    },
    800
  );
  // Update function to handle employee selection
  const handleEmployeeSelect = async (employee: any, setFieldValue: any) => {
    // Set basic information
    setFieldValue('name', employee.name);
    setFieldValue('work_email', employee.work_email);
    setFieldValue('mobile_phone', employee.mobile_phone);
    setFieldValue('id', employee.id);

    try {
      // Fetch and set territory data
      if (employee.territory_ids?.length) {
        const territoryPromises = employee.territory_ids.map((id: number) =>
          territoriesAPI.getById(id)
        );
        const territoryResponses = await Promise.all(territoryPromises);
        const validTerritories = territoryResponses
          .filter((res: any) => res.success)
          .map((res: any) => res.data);

        setTerritories(validTerritories);
        setFieldValue('territory_ids', employee.territory_ids);
        setSelectedTerritories(validTerritories);
      }

      // Fetch and set warehouse data
      if (employee.warehouse_ids?.length) {
        const warehousePromises = employee.warehouse_ids.map((id: number) =>
          warehousesAPI.getById(id)
        );
        const warehouseResponses = await Promise.all(warehousePromises);
        const validWarehouses = warehouseResponses
          .filter((res: any) => res.success)
          .map((res: any) => res.data);

        setWarehouses(validWarehouses);
        setFieldValue('warehouse_ids', employee.warehouse_ids);
        setSelectedWarehouses(validWarehouses);
      }

      // Fetch and set vehicle data
      if (employee.vehicle_id) {
        const vehicleRes = await vehicleAPI.getById(employee.vehicle_id);
        if (vehicleRes.success) {
          setVehicles([vehicleRes.data]);
          setFieldValue('vehicle_id', employee.vehicle_id);
          setSelectedVehicle(vehicleRes?.data?.name);
        }
      }

      // Fetch and set manager data
      if (employee.report_to) {
        const managerRes = await employeeAPI.getById(employee.report_to);
        if (managerRes.success) {
          // setManagers([managerRes.data]);
          setFieldValue('report_to', employee.report_to);
          setSelectedManager(managerRes.data.name);
        }
      }

      // Clear search results after selection
      setSearchResults([]);
    } catch (error) {
      console.error('Error fetching related data:', error);
    }
  };

  const isEditMode = Boolean(initialValues?.id);

  // if region manager is selected, fetch All territories by region
  useEffect(() => {
    if (
      selectedRegionId &&
      selectedManagerType.regionManager &&
      !isEditMode &&
      !selectedTerritories.length // Only fetch if no territories are manually selected
    ) {
      const fetchTerritoriesByRegion = async () => {
        try {
          const response = await territoriesAPI.getByRegionId(
            Number(selectedRegionId)
          );
          if (response.success && response.data && formikRef.current) {
            // Only update if form hasn't been manually populated with territories
            if (!formikRef.current.values.territory_ids?.length) {
              setSelectedTerritories(response.data || []);
              formikRef.current.setFieldValue(
                'territory_ids',
                response.data.map((t: any) => t.id)
              );
            }
          }
        } catch (error) {
          console.error('Error fetching territories by region:', error);
        }
      };
      fetchTerritoriesByRegion();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRegionId,
    selectedManagerType.regionManager,
    isEditMode,
    selectedTerritories.length,
  ]);

  const handleSubmit = async (
    values: any,
    { setSubmitting, setErrors }: any
  ) => {
    try {
      const formattedValues = {
        ...values,
        territory_ids: values.territory_ids.map(Number),
        warehouse_ids: values.warehouse_ids.map(Number),
        report_to: values.is_region_manager
          ? Number(values.id) || null // Region managers report to themselves
          : Number(values.report_to) || null,
        vehicle_id: values.vehicle_id ? Number(values.vehicle_id) : '',
        meter_reading: values.meter_reading ? Number(values.meter_reading) : '',
        company_id: Number(project?.id),
        project_id: Number(project?.id),
        employee_type: 'employee',
        id: values.id ? Number(values.id) : undefined,
        region_id: values.region_id || '',
        is_territory_manager: values.is_territory_manager || false,
        manager_id: values.is_region_manager
          ? Number(values.id)
          : values.report_to,
        is_region_manager: values.is_region_manager || false,
      };

      if (selectedTerritories.length > 0) {
        formattedValues.territory_ids = selectedTerritories.map((t) => t.id);
      }

      await onSubmit(formattedValues);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          'An error occurred during submission'
      );
      // Check if the error is a duplicate email error
      if (error.response?.data?.error === 'DUPLICATE_EMAIL') {
        setErrors({
          work_email: 'This email address is already in use',
          submit: 'Email address is already registered',
        });
        return;
      }
      // Handle other errors
      setErrors({
        submit:
          error.response?.data?.message ||
          error.message ||
          'An error occurred during submission',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        id: initialValues?.id || formikRef.current?.values?.id || '',
        name: initialValues?.name || '',
        work_email: initialValues?.work_email || '',
        mobile_phone: initialValues?.mobile_phone || '',
        territory_ids: initialValues?.territory_ids,
        warehouse_ids: initialValues?.warehouse_ids || [],
        report_to: initialValues?.report_to || '',
        vehicle_id: initialValues?.vehicle_id || '',
        meter_reading: initialValues?.meter_reading || '',
        is_territory_manager: initialValues?.is_territory_manager || false,
        is_region_manager: initialValues?.is_region_manager || false,
        region_id: initialValues?.region_id || '',
        password: initialValues?.password || '',
        company_id: project?.id || '',
        project_id: project?.id || '',
        employee_type: 'employee',
        is_edit_mode: isEditMode,
      }}
      validationSchema={EmployeeSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, setFieldValue, errors, touched, values }) => {
        return (
          <Form className="space-y-8 bg-white dark:bg-gray-800 px-6 py-8 rounded-lg shadow-md max-h-[85vh] overflow-y-auto">
            {/* Employee Search Section - Only show in Create mode */}
            {!isEditMode && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white pb-2 border-b dark:border-gray-700">
                  Search Existing Employee
                </h3>
                <div className="relative">
                  {' '}
                  <input
                    type="text"
                    className="form-input w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    placeholder="Search by name, email, or phone..."
                    autoComplete="off"
                    onChange={(e) => {
                      handleEmployeeSearch(e.target.value);
                    }}
                  />
                  {searchLoading.employee && (
                    <div className="absolute right-2 top-2">
                      <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
                    </div>
                  )}
                  {searchResults.length > 0 && (
                    <button
                      type="button"
                      className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setSearchResults([]);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 max-h-60 overflow-auto">
                      {searchResults.map((employee) => (
                        <div
                          key={employee.id}
                          className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                          onClick={() => {
                            handleEmployeeSelect(employee, setFieldValue);
                            setSearchResults([]); // Clear results after selection
                          }}
                        >
                          <div className="font-medium dark:text-white">
                            {employee.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {employee.work_email}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Basic Information */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white pb-2 border-b dark:border-gray-700">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                {/* Name Field */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Name *
                  </label>
                  <Field
                    name="name"
                    type="text"
                    className={`form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 ${
                      errors.name && touched.name ? 'border-red-500' : ''
                    }`}
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="mt-1 text-sm text-red-600"
                  />
                </div>
                {/* Email Field */}
                <div>
                  <label
                    htmlFor="work_email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Email *
                  </label>
                  <Field
                    name="work_email"
                    type="email"
                    className={`form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 ${
                      errors.work_email && touched.work_email
                        ? 'border-red-500'
                        : ''
                    }`}
                  />
                  <ErrorMessage
                    name="work_email"
                    component="div"
                    className="mt-1 text-sm text-red-600"
                  />
                </div>
                {/* Mobile Phone Field */}
                <div>
                  <label
                    htmlFor="mobile_phone"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Mobile Phone *
                  </label>
                  <Field
                    name="mobile_phone"
                    type="text"
                    className={`form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 ${
                      errors.mobile_phone && touched.mobile_phone
                        ? 'border-red-500'
                        : ''
                    }`}
                  />
                  <ErrorMessage
                    name="mobile_phone"
                    component="div"
                    className="mt-1 text-sm text-red-600"
                  />
                </div>
                {/* Manager Search */}
                {!values.is_region_manager && (
                  <div>
                    <label
                      htmlFor="manager_search"
                      className="form-label dark:text-gray-300"
                    >
                      Manager *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="manager_search"
                        className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                        placeholder="Search manager..."
                        value={selectedManager}
                        onChange={(e) => {
                          setSelectedManager(e.target.value);
                          handleSearch('manager', e.target.value);
                        }}
                      />

                      {searchLoading.manager && (
                        <div className="absolute right-2 top-2">
                          <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
                        </div>
                      )}

                      {/* Manager Search Results */}
                      {managers.length > 0 && selectedManager && (
                        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 max-h-60 overflow-auto">
                          {managers.map((manager) => (
                            <div
                              key={manager.id}
                              className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                              onClick={() => {
                                setFieldValue('report_to', manager.id);

                                setSelectedManager(manager.name);
                                setManagers([]); // Clear search results after selection
                              }}
                            >
                              <span className="dark:text-white">
                                {manager.name}
                              </span>
                              {manager.work_email && (
                                <span className="text-gray-500 dark:text-gray-400 text-sm ml-2">
                                  ({manager.work_email})
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedManager && (
                        <button
                          type="button"
                          className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                          onClick={() => {
                            setFieldValue('report_to', '');
                            setSelectedManager('');
                            setManagers([]);
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <Field type="hidden" name="report_to" />
                    <ErrorMessage
                      name="report_to"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                )}
                {/* Password Field */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Password {!isEditMode && '*'}
                  </label>
                  <div className="relative">
                    <Field
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      className={`form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 ${
                        errors.password && touched.password
                          ? 'border-red-500'
                          : ''
                      }`}
                      placeholder={
                        isEditMode
                          ? 'Leave blank to keep current password'
                          : 'Enter password'
                      }
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-gray-500"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-gray-500"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="mt-1 text-sm text-red-600"
                  />
                </div>
              </div>
            </div>
            {/* Location & Assignment */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white pb-2 border-b dark:border-gray-700">
                Location & Assignment
              </h3>
              <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                {' '}
                {/* Is territory or Region Manager */}{' '}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      Territory Manager
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Assign as territory manager
                    </span>
                  </div>
                  <Switch
                    checked={values.is_territory_manager}
                    onChange={(checked: boolean) => {
                      setFieldValue('is_territory_manager', checked);

                      if (checked) {
                        // If territory manager is selected, uncheck region manager
                        setFieldValue('is_region_manager', false);
                        setSelectedManagerType({
                          territoryManager: true,
                          regionManager: false,
                        });
                        // Territory managers still need managers, so don't clear the manager field
                      } else {
                        // If unchecked, update manager type
                        setSelectedManagerType((prev) => ({
                          ...prev,
                          territoryManager: false,
                        }));
                      }
                    }}
                    className={`${
                      values.is_territory_manager
                        ? 'bg-primary'
                        : 'bg-gray-200 dark:bg-gray-600'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                  >
                    <span
                      className={`${
                        values.is_territory_manager
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      Region Manager
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Assign as region manager
                    </span>
                  </div>
                  <Switch
                    checked={values.is_region_manager}
                    onChange={(checked: boolean) => {
                      setFieldValue('is_region_manager', checked);

                      if (checked) {
                        // If region manager is selected, uncheck territory manager
                        setFieldValue('is_territory_manager', false);
                        setSelectedManagerType({
                          territoryManager: false,
                          regionManager: true,
                        });
                        // Set themselves as their own manager
                        if (values.id) {
                          setFieldValue('report_to', values.id);
                          setSelectedManager(values.name || '');
                        } else {
                          // If no ID yet (new employee), clear manager field
                          setFieldValue('report_to', '');
                          setSelectedManager('');
                        }
                        setManagers([]);
                      } else {
                        // If unchecked, clear manager and update manager type
                        setFieldValue('report_to', '');
                        setSelectedManager('');
                        setManagers([]);
                        setSelectedManagerType((prev) => ({
                          ...prev,
                          regionManager: false,
                        }));
                      }
                    }}
                    className={`${
                      values.is_region_manager
                        ? 'bg-primary'
                        : 'bg-gray-200 dark:bg-gray-600'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                  >
                    <span
                      className={`${
                        values.is_region_manager
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>
                {/* Region Search */}
                <div>
                  <label
                    htmlFor="region_search"
                    className="form-label dark:text-gray-300"
                  >
                    Region *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="region_search"
                      className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      placeholder="Search Region..."
                      value={selectedRegion}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedRegion(value);
                        debouncedRegionSearch(value);
                      }}
                    />

                    {searchLoading.region && (
                      <div className="absolute right-2 top-2">
                        <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
                      </div>
                    )}

                    {/* Manager Search Results */}
                    {regions.length > 0 && selectedRegion && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 max-h-60 overflow-auto">
                        {regions.map((region) => (
                          <div
                            key={region.id}
                            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                            onClick={() => {
                              setFieldValue('region_id', region.id);
                              setSelectedRegionId(region.id.toString());
                              setSelectedRegion(region.name);
                              setRegions([]); // Clear search results after selection
                            }}
                          >
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {region.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedRegion && (
                      <button
                        type="button"
                        className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                        onClick={() => {
                          setFieldValue('region_id', '');
                          setSelectedRegion('');
                          setRegions([]);
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <Field type="hidden" name="region_id" />
                  <ErrorMessage
                    name="region_id"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
                {/* Territory Search */}
                <div>
                  <label
                    htmlFor="territory_search"
                    className="form-label dark:text-gray-300"
                  >
                    Territories *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      disabled={!selectedRegion}
                      id="territory_search"
                      className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      placeholder="Search territories..."
                      value={territorySearchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTerritorySearchTerm(value);
                        debouncedTerritorySearch(value);
                      }}
                    />

                    {searchLoading.territory && (
                      <div className="absolute right-2 top-2">
                        <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
                      </div>
                    )}

                    {territories.length > 0 && territorySearchTerm && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 max-h-60 overflow-auto">
                        {territories.map((territory) => {
                          const isSelected = selectedTerritories.some(
                            (t) => t.id === territory.id
                          );
                          return (
                            <div
                              key={territory.id}
                              className={`px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer ${
                                isSelected ? 'bg-gray-50 dark:bg-gray-600' : ''
                              }`}
                              onClick={() => {
                                if (!isSelected) {
                                  const newSelectedTerritories = [
                                    ...selectedTerritories,
                                    territory,
                                  ];
                                  setSelectedTerritories(
                                    newSelectedTerritories
                                  );
                                  setFieldValue(
                                    'territory_ids',
                                    newSelectedTerritories.map((t) => t.id)
                                  );
                                }
                                setTerritorySearchTerm('');
                                setTerritories([]);
                              }}
                            >
                              <span className="dark:text-white">
                                {territory.name}
                              </span>
                              {isSelected && (
                                <span className="ml-2 text-green-600 dark:text-green-400">
                                  ✓
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Selected Territories Display */}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedTerritories.map((territory) => (
                      <div
                        key={territory.id}
                        className="inline-flex items-center bg-gray-100 dark:bg-gray-700 rounded-full px-3 py-1"
                      >
                        <span className="text-sm dark:text-white">
                          {territory.name}
                        </span>
                        <button
                          type="button"
                          className="ml-2 text-gray-400 hover:text-gray-600"
                          onClick={() => {
                            const newSelectedTerritories =
                              selectedTerritories.filter(
                                (t) => t.id !== territory.id
                              );
                            setSelectedTerritories(newSelectedTerritories);
                            setFieldValue(
                              'territory_ids',
                              newSelectedTerritories.map((t) => t.id)
                            );
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <Field type="hidden" name="territory_ids" />
                  <ErrorMessage
                    name="territory_ids"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
                {/* Warehouse Search */}
                <div>
                  <label
                    htmlFor="warehouse_search"
                    className="form-label dark:text-gray-300"
                  >
                    Warehouses *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="warehouse_search"
                      className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      placeholder="Search warehouses..."
                      value={warehouseSearchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        setWarehouseSearchTerm(value);
                        debouncedWarehouseSearch(value);
                      }}
                    />

                    {searchLoading.warehouse && (
                      <div className="absolute right-2 top-2">
                        <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
                      </div>
                    )}
                    {/* Warehouse Search Results */}
                    {warehouses.length > 0 && warehouseSearchTerm && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 max-h-60 overflow-auto">
                        {warehouses.map((warehouse) => {
                          const isSelected = selectedWarehouses.some(
                            (w) => w.id === warehouse.id
                          );
                          return (
                            <div
                              key={warehouse.id}
                              className={`px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer ${
                                isSelected ? 'bg-gray-50 dark:bg-gray-600' : ''
                              }`}
                              onClick={() => {
                                if (!isSelected) {
                                  const newSelectedWarehouses = [
                                    ...selectedWarehouses,
                                    warehouse,
                                  ];
                                  setSelectedWarehouses(newSelectedWarehouses);
                                  setFieldValue(
                                    'warehouse_ids',
                                    newSelectedWarehouses.map((w) => w.id)
                                  );
                                }
                                setWarehouseSearchTerm('');
                                setWarehouses([]);
                              }}
                            >
                              <span className="dark:text-white">
                                {warehouse.display_name || warehouse.name}
                              </span>
                              {isSelected && (
                                <span className="ml-2 text-green-600 dark:text-green-400">
                                  ✓
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Selected Warehouses Display */}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedWarehouses.map((warehouse) => (
                      <div
                        key={warehouse.id}
                        className="inline-flex items-center bg-gray-100 dark:bg-gray-700 rounded-full px-3 py-1"
                      >
                        <span className="text-sm dark:text-white">
                          {warehouse.display_name || warehouse.name}
                        </span>
                        <button
                          type="button"
                          className="ml-2 text-gray-400 hover:text-gray-600"
                          onClick={() => {
                            const newSelectedWarehouses =
                              selectedWarehouses.filter(
                                (w) => w.id !== warehouse.id
                              );
                            setSelectedWarehouses(newSelectedWarehouses);
                            setFieldValue(
                              'warehouse_ids',
                              newSelectedWarehouses.map((w) => w.id)
                            );
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  {/* Hidden field for form submission */}
                  <Field type="hidden" name="warehouse_ids" />
                  <ErrorMessage
                    name="warehouse_ids"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
                {/* Vehicle Search */}
                <div>
                  <label
                    htmlFor="vehicle_search"
                    className="form-label dark:text-gray-300"
                  >
                    Vehicle
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      placeholder="Search vehicle..."
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedVehicle(value);
                        debouncedVehicleSearch(value);
                      }}
                      value={selectedVehicle}
                    />
                    {searchLoading.vehicle && (
                      <div className="absolute right-2 top-2">
                        <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
                      </div>
                    )}
                    {vehicles.length > 0 && selectedVehicle && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 max-h-60 overflow-auto">
                        {vehicles.map((vehicle) => (
                          <div
                            key={vehicle.id}
                            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer dark:text-white"
                            onClick={() => {
                              setFieldValue('vehicle_id', vehicle.vehicle_id);
                              setSelectedVehicle(vehicle.name || '');
                              setVehicles([]);
                            }}
                          >
                            {vehicle.name}
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedVehicle && (
                      <button
                        type="button"
                        className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                        onClick={() => {
                          setFieldValue('vehicle_id', '');
                          setSelectedVehicle('');
                          setVehicles([]);
                          console.log('Cleared vehicle_id');
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <Field type="hidden" name="vehicle_id" />
                  <ErrorMessage
                    name="vehicle_id"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
                {/* Add Meter Reading field */}
                <div>
                  <label
                    htmlFor="meter_reading"
                    className="form-label dark:text-gray-300"
                  >
                    Meter Reading
                  </label>
                  <Field
                    name="meter_reading"
                    type="text"
                    className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    placeholder="Enter meter reading"
                  />
                  <ErrorMessage
                    name="meter_reading"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
              </div>
            </div>
            {/* Add hidden fields for company_id and project_id */}
            <Field type="hidden" name="company_id" />
            <Field type="hidden" name="project_id" />
            <Field type="hidden" name="employee_type" />
            {/* Form Actions */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
